/**
 * Nervous System content — all option lists and copy for the five
 * stages, opening check-in, Ways to Return toolkit, and completion.
 *
 * Kept in one file so the writing can be adjusted without touching UI.
 */

export const CHECKIN_OPTIONS = [
  "Tension",
  "Restlessness",
  "Heaviness",
  "Warmth",
  "Tightness",
  "Shallow breathing",
  "Racing thoughts",
  "Low energy",
  "Wanting to move",
  "Wanting to withdraw",
  "Feeling distracted",
  "Feeling settled",
  "Feeling open",
  "Feeling connected",
];

export const LOCATION_OPTIONS = [
  "Jaw",
  "Throat",
  "Chest",
  "Shoulders",
  "Belly",
  "Hands",
  "Back",
  "Legs",
  "Head",
  "Everywhere",
];

export const CONTEXT_OPTIONS = [
  "Conflict",
  "Uncertainty",
  "Being rushed",
  "Too much stimulation",
  "Feeling misunderstood",
  "Pressure",
  "Work",
  "Relationships",
  "Social situations",
  "Being alone",
  "Decision-making",
  "Change",
  "Lack of sleep",
  "Anticipation",
  "Excitement",
  "Attraction",
  "Creativity",
  "Connection",
  "Novelty",
];

export const RESPONSE_OPTIONS = [
  "I overthink",
  "I try to solve everything",
  "I get very busy",
  "I seek reassurance",
  "I withdraw",
  "I become irritable",
  "I distract myself",
  "I scroll",
  "I want to leave",
  "I become quiet",
  "I talk more",
  "I try to control the situation",
  "I ignore what I'm noticing",
  "I reach for connection",
  "I move my body",
  "I become unsure what to do",
];

export const REACH_FOR_OPTIONS = [
  "Scroll",
  "Snack",
  "Coffee or caffeine",
  "Work",
  "A drink",
  "Reassurance from someone",
  "Distraction",
  "Silence",
  "A walk",
  "Music",
  "Overthinking",
  "Something else",
];

export const SUPPORT_OPTIONS = [
  "Walking",
  "Movement",
  "Being outside",
  "Quiet",
  "Music",
  "Writing",
  "Breathing",
  "Rest",
  "Food",
  "Water",
  "Warmth",
  "Cool air",
  "A shower or bath",
  "Being alone",
  "Talking to someone",
  "Physical affection",
  "Creating something",
  "Stretching",
  "Reducing stimulation",
  "Changing environments",
  "Taking space",
  "Doing nothing for a moment",
];

export const RESPOND_OPTIONS = [
  "Move",
  "Rest",
  "Step outside",
  "Drink water",
  "Reduce stimulation",
  "Take space",
  "Reach out",
  "Write",
  "Breathe",
  "Stretch",
  "Sit quietly",
  "Eat something",
  "Listen to music",
  "Do nothing for a moment",
];

export const COMPLETION_OPTIONS = [
  { key: "shifted", label: "Something shifted" },
  { key: "noticed_new", label: "I noticed something new" },
  { key: "same", label: "I feel about the same" },
  { key: "unsure", label: "I'm not sure yet" },
];

export const CONNECTION_OPTIONS = [
  { key: "more", label: "More connected" },
  { key: "less", label: "Less connected" },
  { key: "unsure", label: "Unsure" },
];

// The five stages — displayed as a small progress rail.
export const STAGES = [
  {
    key: "notice",
    numeral: "I",
    title: "Notice",
    subtitle: "What is happening in my body?",
    principle: "Notice before interpretation.",
  },
  {
    key: "recognize",
    numeral: "II",
    title: "Recognize",
    subtitle: "When does this tend to happen?",
    principle: "You are noticing a pattern, not a cause.",
  },
  {
    key: "understand",
    numeral: "III",
    title: "Understand",
    subtitle: "What tends to happen next?",
    principle:
      "Recognizing what you tend to do next can help create space between an automatic response and an intentional one.",
  },
  {
    key: "discover",
    numeral: "IV",
    title: "Discover",
    subtitle: "What actually supports me?",
    principle:
      "The things you reach for and the things that support you are not always the same.",
  },
  {
    key: "respond",
    numeral: "V",
    title: "Respond",
    subtitle: "What would support me right now?",
    principle: "One small response is enough.",
  },
];

// Ways to Return — small grounding toolkit. Not a duplicate of Breath.
export const WAYS_TO_RETURN = [
  {
    slug: "orient",
    title: "Orient",
    tagline: "Reconnect with where you are.",
    body:
      "Let your eyes travel slowly around the room. Notice the light. The shapes. The colors. Take in one sound at a time. Feel where your body is being supported — the chair, the floor, the wall behind you. Nothing else is required.",
  },
  {
    slug: "ground",
    title: "Ground",
    tagline: "Come back into your body.",
    body:
      "Feel your feet — the pressure, the contact, the weight. Gently press down through them, then release. Notice what your body reports without trying to interpret it. Stay for as long as feels right.",
  },
  {
    slug: "soften",
    title: "Soften",
    tagline: "Notice what you may be holding.",
    body:
      "Bring quiet attention to your jaw, your shoulders, your hands, your abdomen. Ask: where am I holding more than I need to? Then: what would five percent softer feel like? You are not aiming for complete relaxation. Just a small letting-down.",
  },
  {
    slug: "settle",
    title: "Settle",
    tagline: "Give your attention somewhere to land.",
    body:
      "Choose one sense — see, hear, or touch. Notice a few neutral or pleasant sensations. Let your attention rest there for a breath or two. You are not trying to feel better. You are simply giving your attention somewhere to land.",
  },
];
